import React from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../../i18n/LocalizedLink';
import { BlogLayout } from '../../templates/BlogLayout';
import { SEOMeta } from '../../templates/SEOMeta';
import ArticleContainer from '../../components/layout/ArticleContainer';
import BlogCard from '../../components/layout/BlogCard';

const BlogHomeEs: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="Cómo jugar a Impostor Game: Guía y tutorial completo"
        description="Guía oficial de Impostor Game. Aprende qué es el juego, cómo se juega paso a paso y todas las opciones disponibles para disfrutar con amigos."
        canonical="https://el-impostor.com/blog"
        keywords="impostor game guía, cómo jugar impostor, tutorial impostor, impostor game tutorial"
      />
      <ArticleContainer>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Cómo jugar a Impostor Game: Guía y tutorial completo
        </h1>
        
        <p className="text-lg text-gray-700 dark:text-gray-200 leading-relaxed mb-6">
          Bienvenido a la guía oficial de Impostor Game. Aquí aprenderás qué es el juego, 
          cómo se juega paso a paso y todas las opciones disponibles para disfrutar con amigos, 
          en clase o en eventos.
        </p>

        {/* Sección de navegación a otras páginas del blog */}
        <div className="bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-950 border-2 border-blue-200 dark:border-gray-800 rounded-xl p-6 mb-8">
          <h2 className="text-xl font-semibold text-indigo-800 dark:text-indigo-200 mb-4">
            📚 Explora más sobre Impostor Game
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BlogCard
              icon="🎮"
              title="Modalidades de Juego"
              description="Descubre las formas de jugar"
              link="/blog/modalidad"
            />
            <BlogCard
              icon="🌐"
              title="Modo Multijugador"
              description="Juega en tiempo real"
              link="/blog/modalidad/multijugador"
            />
          </div>
        </div>

        {/* ¿Qué es Impostor Game? */}
        <section className="mb-8">
          <h2 id="que-es" className="text-2xl font-bold text-indigo-800 mb-3">
            ¿Qué es Impostor Game?
          </h2>
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed mb-3">
            Impostor Game es un juego social por rondas en el que algunos jugadores son impostores. 
            Todos escriben mensajes, debaten y, al final de cada ronda, votan para expulsar a la 
            persona sospechosa.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Ideal para 3 a 10 jugadores</li>
            <li>Partidas rápidas en tiempo real</li>
            <li>Perfecto para romper el hielo, clases y streamings</li>
          </ul>
        </section>

        {/* Cómo jugar */}
        <section className="mb-8">
          <h2 id="como-jugar" className="text-2xl font-bold text-indigo-800 mb-3">
            Cómo jugar (paso a paso)
          </h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Crea una sala nueva o únete con un código.</li>
            <li>Se asignan roles automáticamente: jugadores e impostores.</li>
            <li>Escribe tu mensaje y lee a los demás.</li>
            <li>Debate con el grupo y busca inconsistencias.</li>
            <li>Vota para expulsar a un sospechoso al final de la ronda.</li>
            <li>Gana el equipo que cumpla su objetivo.</li>
          </ol>
        </section>

        {/* Callout - Botones de acción */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-6 mb-8">
          <strong className="text-lg text-indigo-900 dark:text-indigo-200 block mb-3">
            ¿Listo para jugar?
          </strong>
          <div className="flex flex-wrap gap-3">
            <LocalizedLink 
              to="/create" 
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Crear sala
            </LocalizedLink>
            <LocalizedLink 
              to="/join" 
              className="px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors"
            >
              Unirme a una sala
            </LocalizedLink>
            <LocalizedLink 
              to="/" 
              className="px-6 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Volver al inicio
            </LocalizedLink>
          </div>
        </div>

        {/* Roles del juego */}
        <section className="mb-8">
          <h2 id="roles" className="text-2xl font-bold text-indigo-800 mb-3">
            Roles del juego
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>
              <strong>Jugador</strong>: identifica comportamientos sospechosos y vota con estrategia.
            </li>
            <li>
              <strong>Impostor</strong>: mezcla tus mensajes, crea dudas y evita ser descubierto.
            </li>
          </ul>
        </section>

        {/* Mecánicas de juego */}
        <section className="mb-8">
          <h2 id="mecanicas" className="text-2xl font-bold text-indigo-800 mb-3">
            Mecánicas de juego
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Rondas con límite de tiempo para escribir y debatir.</li>
            <li>Votaciones anónimas con conteo automático.</li>
            <li>Revelación del expulsado y avance de ronda.</li>
            <li>Multisala para jugar con varios grupos.</li>
          </ul>
        </section>

        {/* Opciones y configuración */}
        <section className="mb-8">
          <h2 id="opciones" className="text-2xl font-bold text-indigo-800 mb-3">
            Opciones y configuración
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Número de jugadores recomendado: 3 a 10.</li>
            <li>Asignación automática de roles para equilibrio.</li>
            <li>Chat por rondas con control de ritmo.</li>
            <li>Desempates de votación automáticos.</li>
          </ul>
        </section>

        {/* Consejos para ganar */}
        <section className="mb-8">
          <h2 id="consejos" className="text-2xl font-bold text-indigo-800 mb-3">
            Consejos para ganar
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Observa el lenguaje y las contradicciones.</li>
            <li>Haz preguntas abiertas para forzar explicaciones.</li>
            <li>Como impostor, mantén mensajes creíbles.</li>
          </ul>
        </section>

        {/* FAQ */}
        <section className="mb-8">
          <h2 id="faq" className="text-2xl font-bold text-indigo-800 mb-3">
            Preguntas frecuentes
          </h2>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Necesito cuenta?</p>
              <p className="text-gray-700 dark:text-gray-200">No, basta con un nombre para entrar a una sala.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Funciona en móvil?</p>
              <p className="text-gray-700 dark:text-gray-200">Sí, está optimizado para móviles.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">¿Cuánto dura una partida?</p>
              <p className="text-gray-700 dark:text-gray-200">Entre 5 y 15 minutos, según jugadores.</p>
            </div>
          </div>
        </section>

        {/* Callout final */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-6">
          <strong className="text-lg text-indigo-900 dark:text-indigo-200 block mb-3">
            ¿Listo para empezar a jugar?
          </strong>
          <div className="flex flex-wrap gap-3">
            <LocalizedLink 
              to="/create" 
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              🚀 Crear sala ahora
            </LocalizedLink>
            <LocalizedLink 
              to="/join" 
              className="px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors"
            >
              Unirme con código
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogHomePt: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="Como jogar Impostor Game: guia e tutorial completo"
        description="Guia oficial do Impostor Game. Aprenda o que é o jogo, como jogar passo a passo e todas as opções disponíveis para aproveitar com amigos."
        canonical="https://el-impostor.com/blog"
        keywords="impostor game guia, como jogar impostor, tutorial impostor, impostor game tutorial"
      />
      <ArticleContainer>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Como jogar Impostor Game: guia e tutorial completo
        </h1>
        
        <p className="text-lg text-gray-700 dark:text-gray-200 leading-relaxed mb-6">
          Bem-vindo ao guia oficial do Impostor Game. Aqui você vai aprender o que é o jogo,
          como jogar passo a passo e todas as opções disponíveis para aproveitar com amigos,
          em sala de aula ou em eventos.
        </p>

        {/* Seção de navegação a outras páginas do blog */}
        <div className="bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-950 border-2 border-blue-200 dark:border-gray-800 rounded-xl p-6 mb-8">
          <h2 className="text-xl font-semibold text-indigo-800 dark:text-indigo-200 mb-4">
            📚 Explore mais sobre o Impostor Game
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BlogCard
              icon="🎮"
              title="Modalidades de Jogo"
              description="Descubra as formas de jogar"
              link="/blog/modalidad"
            />
            <BlogCard
              icon="🌐"
              title="Modo Multijogador"
              description="Jogue em tempo real"
              link="/blog/modalidad/multijugador"
            />
          </div>
        </div>

        {/* O que é Impostor Game? */}
        <section className="mb-8">
          <h2 id="que-es" className="text-2xl font-bold text-indigo-800 mb-3">
            O que é Impostor Game?
          </h2>
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed mb-3">
            Impostor Game é um jogo social por rodadas em que alguns jogadores são impostores.
            Todos escrevem mensagens, debatem e, ao final de cada rodada, votam para expulsar a
            pessoa suspeita.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Ideal para 3 a 10 jogadores</li>
            <li>Partidas rápidas em tempo real</li>
            <li>Perfeito para quebra-gelos, aulas e streaming</li>
          </ul>
        </section>

        {/* Como jogar */}
        <section className="mb-8">
          <h2 id="como-jugar" className="text-2xl font-bold text-indigo-800 mb-3">
            Como jogar (passo a passo)
          </h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Crie uma sala nova ou entre com um código.</li>
            <li>Os papéis são atribuídos automaticamente: jogadores e impostores.</li>
            <li>Escreva sua mensagem e leia a dos outros.</li>
            <li>Debata com o grupo e procure inconsistências.</li>
            <li>Vote para expulsar um suspeito ao final da rodada.</li>
            <li>Vence a equipe que cumprir seu objetivo.</li>
          </ol>
        </section>

        {/* Callout - Botões de ação */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-6 mb-8">
          <strong className="text-lg text-indigo-900 dark:text-indigo-200 block mb-3">
            Pronto para jogar?
          </strong>
          <div className="flex flex-wrap gap-3">
            <LocalizedLink 
              to="/create" 
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Criar sala
            </LocalizedLink>
            <LocalizedLink 
              to="/join" 
              className="px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors"
            >
              Entrar em uma sala
            </LocalizedLink>
            <LocalizedLink 
              to="/" 
              className="px-6 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Voltar ao início
            </LocalizedLink>
          </div>
        </div>

        {/* Papéis do jogo */}
        <section className="mb-8">
          <h2 id="roles" className="text-2xl font-bold text-indigo-800 mb-3">
            Papéis do jogo
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>
              <strong>Jogador</strong>: identifique comportamentos suspeitos e vote com estratégia.
            </li>
            <li>
              <strong>Impostor</strong>: misture suas mensagens, crie dúvidas e evite ser descoberto.
            </li>
          </ul>
        </section>

        {/* Mecânicas */}
        <section className="mb-8">
          <h2 id="mecanicas" className="text-2xl font-bold text-indigo-800 mb-3">
            Mecânicas do jogo
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Rodadas com tempo limite para escrever e debater.</li>
            <li>Votações anônimas com contagem automática.</li>
            <li>Revelação de quem foi eliminado e avanço para a próxima rodada.</li>
            <li>Suporte multissala para jogar com vários grupos.</li>
          </ul>
        </section>

        {/* Opções */}
        <section className="mb-8">
          <h2 id="opciones" className="text-2xl font-bold text-indigo-800 mb-3">
            Opções e configuração
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Número recomendado de jogadores: 3 a 10.</li>
            <li>Papéis atribuídos automaticamente para equilibrar a partida.</li>
            <li>Chat por rodadas com controle de ritmo.</li>
            <li>Votação ao final da rodada com desempates automáticos.</li>
          </ul>
        </section>

        {/* Dicas */}
        <section className="mb-8">
          <h2 id="consejos" className="text-2xl font-bold text-indigo-800 mb-3">
            Dicas para vencer
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Observe a linguagem e as contradições.</li>
            <li>Faça perguntas abertas para forçar explicações.</li>
            <li>Como impostor, mantenha mensagens convincentes.</li>
          </ul>
        </section>

        {/* FAQ */}
        <section className="mb-8">
          <h2 id="faq" className="text-2xl font-bold text-indigo-800 mb-3">
            Perguntas frequentes
          </h2>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Preciso de conta?</p>
              <p className="text-gray-700 dark:text-gray-200">Não, basta escolher um nome para entrar em uma sala.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Funciona no celular?</p>
              <p className="text-gray-700 dark:text-gray-200">Sim, é otimizado para dispositivos móveis.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Quanto dura uma partida?</p>
              <p className="text-gray-700 dark:text-gray-200">Cerca de 5 a 15 minutos, dependendo dos jogadores.</p>
            </div>
          </div>
        </section>

        {/* Callout final */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-6">
          <strong className="text-lg text-indigo-900 dark:text-indigo-200 block mb-3">Pronto para começar?</strong>
          <div className="flex flex-wrap gap-3">
            <LocalizedLink
              to="/create"
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              🚀 Criar sala agora
            </LocalizedLink>
            <LocalizedLink
              to="/join"
              className="px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors"
            >
              Entrar com código
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogHomeEn: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="How to Play Impostor Game: Complete Guide & Tutorial"
        description="Official Impostor Game guide. Learn what the game is, how to play step by step, and all available options to enjoy with friends."
        canonical="https://el-impostor.com/blog"
        keywords="impostor game guide, how to play impostor, impostor tutorial, impostor game tutorial"
      />
      <ArticleContainer>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          How to Play Impostor Game: Complete Guide & Tutorial
        </h1>

        <p className="text-lg text-gray-700 dark:text-gray-200 leading-relaxed mb-6">
          Welcome to the official Impostor Game guide. Here you’ll learn what the game is,
          how to play step by step, and all the options available to enjoy with friends,
          in class, or at events.
        </p>

        {/* Navigation to other blog pages */}
        <div className="bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-950 border-2 border-blue-200 dark:border-gray-800 rounded-xl p-6 mb-8">
          <h2 className="text-xl font-semibold text-indigo-800 dark:text-indigo-200 mb-4">
            📚 Explore more about Impostor Game
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BlogCard
              icon="🎮"
              title="Game Modes"
              description="Discover the ways to play"
              link="/blog/modalidad"
            />
            <BlogCard
              icon="🌐"
              title="Multiplayer Mode"
              description="Play in real time"
              link="/blog/modalidad/multijugador"
            />
          </div>
        </div>

        {/* What is Impostor Game? */}
        <section className="mb-8">
          <h2 id="que-es" className="text-2xl font-bold text-indigo-800 mb-3">
            What is Impostor Game?
          </h2>
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed mb-3">
            Impostor Game is a round-based social game where some players are impostors.
            Everyone writes messages, debates, and at the end of each round, votes to
            eliminate the suspicious player.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Great for 3 to 10 players</li>
            <li>Fast-paced real-time matches</li>
            <li>Perfect for icebreakers, classes, and streams</li>
          </ul>
        </section>

        {/* How to play */}
        <section className="mb-8">
          <h2 id="como-jugar" className="text-2xl font-bold text-indigo-800 mb-3">
            How to play (step by step)
          </h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Create a new room or join with a code.</li>
            <li>Roles are assigned automatically: players and impostors.</li>
            <li>Write your message and read others.</li>
            <li>Debate with the group and look for inconsistencies.</li>
            <li>Vote to eliminate a suspect at the end of the round.</li>
            <li>The team that completes its objective wins.</li>
          </ol>
        </section>

        {/* Callout - Action buttons */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-6 mb-8">
          <strong className="text-lg text-indigo-900 dark:text-indigo-200 block mb-3">Ready to play?</strong>
          <div className="flex flex-wrap gap-3">
            <LocalizedLink
              to="/create"
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Create room
            </LocalizedLink>
            <LocalizedLink
              to="/join"
              className="px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors"
            >
              Join a room
            </LocalizedLink>
            <LocalizedLink
              to="/"
              className="px-6 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Back to home
            </LocalizedLink>
          </div>
        </div>

        {/* Roles */}
        <section className="mb-8">
          <h2 id="roles" className="text-2xl font-bold text-indigo-800 mb-3">
            Game roles
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>
              <strong>Player</strong>: identify suspicious behavior and vote strategically.
            </li>
            <li>
              <strong>Impostor</strong>: blend in, create doubt, and avoid getting caught.
            </li>
          </ul>
        </section>

        {/* Mechanics */}
        <section className="mb-8">
          <h2 id="mecanicas" className="text-2xl font-bold text-indigo-800 mb-3">
            Game mechanics
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Timed rounds for writing and debating.</li>
            <li>Anonymous voting with automatic tallying.</li>
            <li>Reveal the eliminated player and advance the round.</li>
            <li>Multiple rooms to play with different groups.</li>
          </ul>
        </section>

        {/* Options */}
        <section className="mb-8">
          <h2 id="opciones" className="text-2xl font-bold text-indigo-800 mb-3">
            Options and settings
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Recommended player count: 3 to 10.</li>
            <li>Automatic role assignment for balance.</li>
            <li>Round-based chat to keep the pace.</li>
            <li>Automatic tie handling in votes.</li>
          </ul>
        </section>

        {/* Tips */}
        <section className="mb-8">
          <h2 id="consejos" className="text-2xl font-bold text-indigo-800 mb-3">
            Tips to win
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200">
            <li>Watch language patterns and contradictions.</li>
            <li>Ask open-ended questions to force explanations.</li>
            <li>As impostor, keep your messages believable.</li>
          </ul>
        </section>

        {/* FAQ */}
        <section className="mb-8">
          <h2 id="faq" className="text-2xl font-bold text-indigo-800 mb-3">
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Do I need an account?</p>
              <p className="text-gray-700 dark:text-gray-200">No—just choose a name to enter a room.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">Does it work on mobile?</p>
              <p className="text-gray-700 dark:text-gray-200">Yes, it’s optimized for mobile devices.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">How long does a match last?</p>
              <p className="text-gray-700 dark:text-gray-200">About 5 to 15 minutes depending on players.</p>
            </div>
          </div>
        </section>

        {/* Final callout */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-6">
          <strong className="text-lg text-indigo-900 dark:text-indigo-200 block mb-3">Ready to start?</strong>
          <div className="flex flex-wrap gap-3">
            <LocalizedLink
              to="/create"
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              🚀 Create a room now
            </LocalizedLink>
            <LocalizedLink
              to="/join"
              className="px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors"
            >
              Join with code
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogHome: React.FC = () => {
  const { i18n } = useTranslation();
  if (i18n.language === 'en') return <BlogHomeEn />;
  if (i18n.language === 'pt') return <BlogHomePt />;
  return <BlogHomeEs />;
};

export default BlogHome;
